/**
 * SharingTab Component
 * Manages event permissions and sharing with other users
 */

import React, { useState } from 'react';
import {
  UserPlusIcon,
  TrashIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  PencilSquareIcon,
  EyeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useEventPermissions, useCanEditEvent, useUpdateEventPermission, useRemoveEventPermission } from '../../../hooks/useEventPermissions';
import { PermissionLevel } from '../../../types/event.types';
import type { UUID } from '../../../types/common.types';
import LoadingSpinner from '../../common/LoadingSpinner';
import Button from '../../common/Button';
import { useToast } from '../../ui/Toast';
import ShareEventModal from '../ShareEventModal';

interface SharingTabProps {
  eventId: UUID;
}

const permissionLabels: Record<PermissionLevel, { label: string; description: string; icon: React.ElementType }> = {
  [PermissionLevel.OWNER]: {
    label: 'Owner',
    description: 'Full access - can edit, share, and manage owners',
    icon: ShieldCheckIcon,
  },
  [PermissionLevel.EDIT]: {
    label: 'Editor',
    description: 'Can edit event details',
    icon: PencilSquareIcon,
  },
  [PermissionLevel.VIEW]: {
    label: 'Viewer',
    description: 'Can view event details only',
    icon: EyeIcon,
  },
};

const SharingTab: React.FC<SharingTabProps> = ({ eventId }) => {
  const { showToast } = useToast();
  const [showShareModal, setShowShareModal] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<UUID | null>(null);

  // Fetch permissions
  const { data: permissions, isLoading, error, refetch } = useEventPermissions(eventId);

  // Get current user's access
  const { canManage, isLoading: isLoadingAccess } = useCanEditEvent(eventId);

  // Mutations
  const updatePermission = useUpdateEventPermission();
  const removePermission = useRemoveEventPermission();

  // Count owners
  const ownerCount = permissions?.filter(p => p.permission_level === PermissionLevel.OWNER).length || 0;

  const handlePermissionChange = async (userId: UUID, newLevel: PermissionLevel) => {
    try {
      await updatePermission.mutateAsync({
        eventId,
        userId,
        permissionLevel: newLevel,
      });
      showToast('success', 'Permission updated');
    } catch (error: any) {
      showToast('error', error.message || 'Failed to update permission');
    }
  };

  const handleRemovePermission = async (userId: UUID) => {
    try {
      await removePermission.mutateAsync({ eventId, userId });
      showToast('success', 'User removed from event');
      setConfirmRemove(null);
    } catch (error: any) {
      showToast('error', error.message || 'Failed to remove user');
    }
  };

  if (isLoading || isLoadingAccess) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-semibold">Error loading permissions</h3>
        <p className="text-red-600 text-sm mt-1">
          {error instanceof Error ? error.message : 'An error occurred'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Sharing & Permissions</h3>
          <p className="text-sm text-gray-500 mt-1">
            Manage who has access to this event
          </p>
        </div>
        {canManage && (
          <Button
            variant="primary"
            size="sm"
            icon={UserPlusIcon}
            onClick={() => setShowShareModal(true)}
          >
            Add User
          </Button>
        )}
      </div>

      {/* Permissions List */}
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
        {permissions?.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <UserCircleIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p>No permissions found</p>
            <p className="text-sm">This event may not have proper ownership configured.</p>
          </div>
        ) : (
          permissions?.map((permission) => {
            const isOwner = permission.permission_level === PermissionLevel.OWNER;
            const isLastOwner = isOwner && ownerCount <= 1;
            const Icon = permissionLabels[permission.permission_level as PermissionLevel]?.icon || UserCircleIcon;

            return (
              <div
                key={permission.id}
                className="p-4 flex items-center justify-between hover:bg-gray-50"
              >
                {/* User Info */}
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-700 font-medium text-sm">
                        {permission.user?.full_name?.charAt(0) || '?'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {permission.user?.full_name || 'Unknown User'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {permission.user?.email || ''}
                    </p>
                  </div>
                </div>

                {/* Permission Controls */}
                <div className="flex items-center space-x-4">
                  {canManage ? (
                    <>
                      {/* Permission Level Dropdown */}
                      <select
                        value={permission.permission_level}
                        onChange={(e) => handlePermissionChange(permission.user_id, e.target.value as PermissionLevel)}
                        disabled={updatePermission.isPending || (isLastOwner && permission.permission_level === PermissionLevel.OWNER)}
                        className={`
                          text-sm border border-gray-300 rounded-md px-3 py-1.5
                          focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                          ${isLastOwner && permission.permission_level === PermissionLevel.OWNER ? 'bg-gray-100 cursor-not-allowed' : ''}
                        `}
                        title={isLastOwner ? 'Cannot change the last owner' : ''}
                      >
                        <option value={PermissionLevel.OWNER}>Owner</option>
                        <option value={PermissionLevel.EDIT}>Editor</option>
                        <option value={PermissionLevel.VIEW}>Viewer</option>
                      </select>

                      {/* Remove Button */}
                      {confirmRemove === permission.user_id ? (
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleRemovePermission(permission.user_id)}
                            disabled={removePermission.isPending || isLastOwner}
                          >
                            Confirm
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setConfirmRemove(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmRemove(permission.user_id)}
                          disabled={isLastOwner}
                          className={`
                            p-2 rounded-md transition-colors
                            ${isLastOwner
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                            }
                          `}
                          title={isLastOwner ? 'Cannot remove the last owner' : 'Remove access'}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </>
                  ) : (
                    /* Read-only badge */
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Icon className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        {permissionLabels[permission.permission_level as PermissionLevel]?.label || permission.permission_level}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Last Owner Warning */}
      {ownerCount === 1 && canManage && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-800 font-medium">
              This event has only one owner
            </p>
            <p className="text-sm text-amber-700 mt-1">
              Add another owner before removing or demoting the current one.
            </p>
          </div>
        </div>
      )}

      {/* Permission Legend */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Permission Levels</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(permissionLabels).map(([level, info]) => {
            const Icon = info.icon;
            return (
              <div key={level} className="flex items-start space-x-2">
                <Icon className="h-5 w-5 text-gray-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-700">{info.label}</p>
                  <p className="text-xs text-gray-500">{info.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <ShareEventModal
          eventId={eventId}
          onClose={() => setShowShareModal(false)}
          onSuccess={() => {
            setShowShareModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default SharingTab;
